<template>
  <form @submit.prevent="submit">
    <p>{{ $t("global.change-password-message") }}</p>
    <FormInput
      type="password"
      name="currentPassword"
      v-model="fields.currentPassword"
      testId="current-password-input"
      :label="`${$t('global.current-password')} *`"
      :errors="validation.fields.currentPassword.errors"
      :dirty="validation.fields.currentPassword.dirty"
      :disabled="loading"
      autocomplete="off"
    />

    <FormInput
      name="email"
      v-model="fields.email"
      testId="email-input"
      :label="`${$t('global.email')} *`"
      :errors="validation.fields.email.errors"
      :dirty="validation.fields.email.dirty"
      :disabled="loading"
    />

    <FormCheckbox
      name="changePass"
      v-model="fields.changePass"
      @change="clearPasswords"
      testId="current-password-input"
      :label="`${$t('global.change-password')}`"
      :errors="validation.fields.changePass.errors"
      :dirty="validation.fields.changePass.dirty"
      :disabled="loading"
    />
    <div v-if="fields.changePass">
      <div class="my-4">
        <div class="row">
          <div class="col-md-6">
            <div class="text-secondary my-2 d-flex">
              <div class="mr-2">{{ $t("global.password-tips") }}:</div>
              <Popover :header="$t('global.password-tips')">
                <Icon icon="help" color="gamma" />
                <template #message>
                  <PasswordTips />
                </template>
              </Popover>
            </div>
          </div>
          <div class="col-md-6">
            <Btn
              @click="generatePassword"
              :disabled="loading || passwordLoading"
              :loading="passwordLoading"
              class="w-100"
            >
              {{ $t("global.generate-password") }}
            </Btn>
          </div>
        </div>
        <Alert
          v-if="generatedPassword"
          variant="light"
          :dismissible="false"
          class="text-center mt-3"
        >
          {{ generatedPassword }} <CopyToClipboard :text="generatedPassword" />
        </Alert>
      </div>
      <div>
        <FormInput
          type="password"
          name="password"
          v-model="fields.password"
          testId="current-password-input"
          :label="`${$t('global.new-password')} *`"
          :errors="validation.fields.password.errors"
          :dirty="validation.fields.password.dirty"
          :disabled="loading"
        />

        <FormInput
          type="password"
          name="passwordConfirm"
          v-model="fields.passwordConfirm"
          testId="current-password-input"
          :label="`${$t('global.confirm-new-password')} *`"
          :errors="validation.fields.passwordConfirm.errors"
          :dirty="validation.fields.passwordConfirm.dirty"
          :disabled="loading"
        />
      </div>
    </div>

    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="loading"
        @click="cancel"
        data-test="cancel-pin-edit-button"
        v-if="isCancellable"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="loading"
        :loading="loading"
        data-test="edit-pin-button"
      >
        {{ $t("global.update-security") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, reactive, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Btn from "@/components/display/Btn";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormInput from "@/components/display/form/FormInput";
import Icon from "@/components/display/icon/Icon";
import PasswordTips from "@/components/display/PasswordTips";
import Popover from "@/components/display/Popover";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { userPassword as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "UserSecurityForm",
  components: {
    FormCheckbox,
    FormInput,
    Btn,
    PasswordTips,
    Popover,
    Alert,
    CopyToClipboard,
    Icon,
  },
  setup() {
    const loading = ref(false);
    const errors = ref(null);
    const user = store.state.auth.user;
    const fields = reactive({
      email: user.email,
      currentPassword: null,
      changePass: false,
      password: null,
      passwordConfirm: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        email: {
          required: "Email is required.",
          email: "Must be a valid email address.",
        },
        currentPassword: {
          required: "Current password is required.",
        },
        password: {
          requiredIf: "New password is required.",
        },
        passwordConfirm: {
          requiredIf: "New password confirmation is required.",
          matchField: "Confirmation must match new password.",
        },
      }
    );

    const isCancellable = computed(() => {
      return user.email !== fields.email || fields.changePass;
    });

    const cancel = () => {
      fields.email = user.email;
      fields.password = null;
      fields.passwordConfirm = null;
      fields.currentPassword = null;
      fields.changePass = false;
      generatedPassword.value = null;
    };

    const passwordLoading = ref(false);
    const generatedPassword = ref(null);

    const generatePassword = () => {
      passwordLoading.value = true;
      store
        .dispatch("password/generate")
        .then((password) => {
          generatedPassword.value = password;
          fields.password = password;
          fields.passwordConfirm = password;
        })
        .finally(() => (passwordLoading.value = false));
    };

    const clearPasswords = () => {
      if (!fields.changePass) {
        fields.password = null;
        fields.passwordConfirm = null;
      }
    };

    const submit = () => {
      if (!isCancellable.value) {
        return;
      }

      errors.value = null;

      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch("auth/editUser", {
              email: fields.email,
              currentPassword: fields.currentPassword,
              password: fields.password,
              passwordConfirm: fields.passwordConfirm,
            })
            .then(() => {
              user.email = fields.email;
              fields.currentPassword = fields.password;
              fields.password = null;
              fields.passwordConfirm = null;
              fields.changePass = null;
              generatedPassword.value = null;

              store.dispatch("toasts/addToast", {
                variant: "success",
                message: "Successfully updated security settings.",
              });
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: "Error updating security settings.",
              });
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };
    return {
      errors,
      fields,
      loading,
      submit,
      validation,
      cancel,
      isCancellable,
      generatePassword,
      generatedPassword,
      passwordLoading,
      clearPasswords,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
